import { useToast } from '@chakra-ui/react';
import React, { useState } from 'react';

import {
  baixarResumoDoAno,
  type IFiltroTopoDaCarteira,
  type IMesDoCalendario,
  type IResumoDoAno,
} from '../../../../../hooks/useFechamentoContabilMensal';
import { NOMES_MES, numero, porcento } from '../regrasDaCarteira';
import { Baixar } from './icones';

const FAIXAS = ['1', '2', '3', '4', '5', '6+'] as const;

/** Uma linha de totais do ano, no lugar da instrução sob o título do calendário. */
export function LinhaDoAno({ r }: { r: IResumoDoAno }) {
  return (
    <span className="anoLinha">
      <b>{numero(r.empresas)}</b> empresas no ano · <b>{numero(r.fechadas)}</b> de{' '}
      {numero(r.a_operar)} fechadas ({porcento(r.fechadas, r.a_operar)}) ·{' '}
      <b>{numero(r.atraso.meses)}</b> meses em aberto em {numero(r.atraso.empresas)} empresas
    </span>
  );
}

function Indicador({
  rotulo,
  valor,
  detalhe,
  definicao,
}: {
  rotulo: string;
  valor: string;
  detalhe?: string;
  definicao: string;
}) {
  return (
    <div className="raInd">
      <span className="raRot">{rotulo}</span>
      <span className="raNum">{valor}</span>
      {detalhe && <span className="raDet">{detalhe}</span>}
      <span className="raDef">{definicao}</span>
    </div>
  );
}

/** Detalhe do ano: volume (item 2), evolução mensal (item 3) e atraso (item 4). */
export function ResumoDoAno({
  ano,
  r,
  meses,
  topo,
}: {
  ano: number;
  r: IResumoDoAno;
  meses: IMesDoCalendario[];
  topo: IFiltroTopoDaCarteira;
}) {
  const toast = useToast();
  const [baixando, setBaixando] = useState(false);
  const exportar = async () => {
    setBaixando(true);
    try {
      await baixarResumoDoAno(ano, topo);
    } catch {
      toast({
        status: 'error',
        title: 'Não foi possível gerar a planilha',
        description: 'Tente de novo em instantes.',
        isClosable: true,
      });
    } finally {
      setBaixando(false);
    }
  };
  const comTotal = meses.filter(m => m.total > 0);
  const maiorFaixa = Math.max(1, ...FAIXAS.map(f => r.atraso.faixas[f] ?? 0));
  return (
    <div className="resumoAno" role="region" aria-label={`Resumo de ${ano}`}>
      <div className="raCab">
        <span className="raTitulo">Resumo de {ano}</span>
        <button
          type="button"
          className="fnExportar"
          onClick={exportar}
          disabled={baixando}
          aria-label={`Exportar a planilha do resumo de ${ano}`}
          title="Resumo do ano, evolução mês a mês e uma linha por empresa com mês em aberto"
        >
          <Baixar tam={12} traco={2.4} />
          {baixando ? 'Gerando…' : 'Exportar'}
        </button>
      </div>

      <div className="raBloco">
        <div className="raSub">Volume</div>
        <div className="raGrade">
          <Indicador
            rotulo="Empresas no ano"
            valor={numero(r.empresas)}
            definicao="Empresas distintas no total de algum mês do ano."
          />
          <Indicador
            rotulo="A operar"
            valor={numero(r.a_operar)}
            detalhe={`${numero(r.empresa_mes)} empresa-mês − ${numero(r.sem_balancete)} sem balancete`}
            definicao="Soma dos meses que a área tem de revisar e fechar."
          />
          <Indicador
            rotulo="Fechadas"
            valor={`${numero(r.fechadas)} · ${porcento(r.fechadas, r.a_operar)}`}
            detalhe={`sistema ${numero(r.fechadas_sistema)} · área ${numero(r.fechadas_area)}`}
            definicao="Fechadas no Domínio; o sistema fecha as sem movimento."
          />
          <Indicador
            rotulo="Liberadas"
            valor={numero(r.liberadas)}
            definicao="Revisadas e prontas: aguardam a área fechar."
          />
          <Indicador
            rotulo="Travadas"
            valor={numero(r.travadas)}
            definicao="Com pendência que impede o fechamento."
          />
        </div>
      </div>

      <div className="raBloco">
        <div className="raSub">Evolução mês a mês</div>
        <div className="raTabela">
          <table>
            <thead>
              <tr>
                <th>Mês</th>
                <th>Total</th>
                <th title="Sem balancete no Domínio">Sem bal.</th>
                <th>A operar</th>
                <th>Fechadas</th>
                <th>% fechado</th>
                <th>Liberadas</th>
                <th>Travadas</th>
                <th>Inativas</th>
                <th title="Clientes desde o mês, no total">Entradas</th>
                <th title="Clientes até o mês, no total (o mês de saída ainda é do escritório)">
                  Saídas
                </th>
              </tr>
            </thead>
            <tbody>
              {comTotal.map(m => {
                const s = m.situacoes;
                const aOperar = m.total - s.sembal.qtd;
                return (
                  <tr key={m.competencia}>
                    <td>{NOMES_MES[Number(m.competencia.slice(5, 7)) - 1].slice(0, 3)}</td>
                    <td>{numero(m.total)}</td>
                    <td>{numero(s.sembal.qtd)}</td>
                    <td>{numero(aOperar)}</td>
                    <td>{numero(s.fech.qtd)}</td>
                    <td>{porcento(s.fech.qtd, aOperar)}</td>
                    <td>{numero(s.lib.qtd)}</td>
                    <td>{numero(s.trav.qtd)}</td>
                    <td>{numero(m.inativas)}</td>
                    <td>{m.funil ? numero(m.funil.entradas) : '—'}</td>
                    <td>{m.funil ? numero(m.funil.saidas) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="raBloco">
        <div className="raSub">Atraso</div>
        <div className="raAtraso">
          <div className="raGrade">
            <Indicador
              rotulo="Empresas com mês em aberto"
              valor={`${numero(r.atraso.empresas)} de ${numero(r.empresas)}`}
              definicao="Ao menos um mês do ano liberado ou travado."
            />
            <Indicador
              rotulo="Meses em aberto"
              valor={numero(r.atraso.meses)}
              definicao="Liberadas + travadas: o que falta fechar no ano."
            />
          </div>
          <div className="raFaixas" aria-label="Empresas por meses em aberto">
            <span className="raRot">Empresas por meses em aberto</span>
            {FAIXAS.map(f => {
              const q = r.atraso.faixas[f] ?? 0;
              return (
                <div key={f} className="raFaixa">
                  <span className="raFaixaRot">{f === '1' ? '1 mês' : `${f} meses`}</span>
                  <span className="raBarra">
                    <span style={{ width: `${(q / maiorFaixa) * 100}%` }} />
                  </span>
                  <span className="raFaixaNum">{numero(q)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
